import { filterOptions } from "@src/helpers/constants";
import { retrieveItem } from "@src/helpers/functions";
import {
  AppDispatch,
  dynamicSet,
  getMyOrganization,
  getNcdRiskAnalysisRequest,
  getReadingListRequest,
  getScreeningStatsRequest,
  getSubjectListRequest,
  NcdRiskAnalysisFilterInput,
  Organization,
  ReadingFilterInput,
} from "@src/redux";
import LevelFilterContainer from "@src/views/components/common/LevelFliter";
import DynamicTreeSelect from "@src/views/components/common/TreeSelect";
import {
  Button,
  Col,
  DatePicker,
  Form,
  Radio,
  Row,
  Select,
  Slider,
  Switch,
  Typography,
} from "antd";
import FormItem from "antd/es/form/FormItem";
import dayjs from "dayjs";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import TripleToggleSwitch from "./TripleSwitch";
import NCDFilterSegmented from "@src/views/components/subject-details/subject-last-screening-filter/NcdFilterSegment";

interface ReadingFilterSideBarProps {}

// Reusable Risk Segment Component using Radio buttons
interface RiskSegmentProps {
  name: string;
  label: string;
  value: string | undefined;
  form: any;
  t: (key: string) => string;
}

const RiskSegment: React.FC<RiskSegmentProps> = ({
  name,
  label,
  value,
  form,
  t,
}) => (
  <FormItem
    name={name}
    label={
      <Col span={12} className="reading-filter-label-without-bold">
        {t(label)}
      </Col>
    }
    layout="horizontal"
  >
    <Col className="segment-container">
      <Radio.Group
        value={value}
        onChange={(e) => form.setFieldValue(name, e.target.value)}
        optionType="button"
        buttonStyle="solid"
        className="risk-radio-group"
        style={{
          width: "100px",
          display: "flex",
          gap: "0px",
          height: "30px",
        }}
      >
        <Radio.Button
          value="na"
          style={{
            flex: 1,
            textAlign: "center",
            borderRadius: "4px 0 0 4px",
            borderRight: "1px solid #d9d9d9",
            height: "30px",
            lineHeight: "30px",
            fontSize: "12px",
            fontWeight: "500",
            padding: "0 8px",
            minWidth: "0",
          }}
        >
          NA
        </Radio.Button>
        <Radio.Button
          value="yes"
          style={{
            flex: 1,
            textAlign: "center",
            borderRadius: "0",
            borderRight: "1px solid #d9d9d9",
            height: "30px",
            lineHeight: "30px",
            fontSize: "12px",
            fontWeight: "500",
            padding: "0 8px",
            minWidth: "0",
          }}
        >
          Yes
        </Radio.Button>
        <Radio.Button
          value="no"
          style={{
            flex: 1,
            textAlign: "center",
            borderRadius: "0 4px 4px 0",
            height: "30px",
            lineHeight: "30px",
            fontSize: "12px",
            fontWeight: "500",
            padding: "0 8px",
            minWidth: "0",
          }}
        >
          No
        </Radio.Button>
      </Radio.Group>
    </Col>
  </FormItem>
);

const { Title, Text } = Typography;

// custom styles for better Radio.Button appearance
const customStyles = `
  .risk-radio-group .ant-radio-button-wrapper {
    border-color: #d9d9d9 !important;
    background: #fafafa !important;
    color: #595959 !important;
    transition: all 0.2s ease !important;
  }
  
  .risk-radio-group .ant-radio-button-wrapper:hover {
    color: #1890ff !important;
    border-color: #1890ff !important;
  }
  
  .risk-radio-group .ant-radio-button-wrapper-checked {
    background: #1890ff !important;
    border-color: #1890ff !important;
    color: white !important;
    box-shadow: none !important;
  }
  
  .risk-radio-group .ant-radio-button-wrapper-checked:hover {
    background: #40a9ff !important;
    border-color: #40a9ff !important;
    color: white !important;
  }
  
  .risk-radio-group .ant-radio-button-wrapper:not(:first-child)::before {
    display: none !important;
  }
`;

// Full domain of the age slider. An untouched slider sits at [MIN, MAX], which means
// "every age" -- not an age filter. Keep these in one place so the slider bounds and the
// "is the user actually filtering?" test cannot drift apart.
const AGE_SLIDER_MIN = 1;
const AGE_SLIDER_MAX = 120;

const ReadingFilterSideBar: React.FC<ReadingFilterSideBarProps> = ({}) => {
  const [form] = Form.useForm();
  const [organizationForm] = Form.useForm();
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [sliderValue, setSliderValue] = useState<[number, number]>([
    AGE_SLIDER_MIN,
    AGE_SLIDER_MAX,
  ]);
  const [subjectFilterValues, setSubjectFilterValues] = useState<any>({});
  const [showOnlyLastReading, setShowOnlyLastReading] =
    useState<boolean>(false);
  const debounceTimerRef = useRef<number | undefined>(undefined);
  const organizationId = Form.useWatch("organizationId", organizationForm);
  const levelAttributeId = Form.useWatch(
    "levelAttributeId",
    organizationForm
  );
  const [riskFilterCategory, setRiskFilterCategory] = useState<string>("");

  const date = Form.useWatch("date", form);
  const gender = Form.useWatch("gender", form);
  const ncd_confirmation_type = Form.useWatch("ncd_confirmation_type", form);
  const respiratoryRisk = Form.useWatch("respiratoryRisk", form);
  const hypertensionRisk = Form.useWatch("hypertensionRisk", form);
  const hypotensionRisk = Form.useWatch("hypotensionRisk", form);
  const diabeticRisk = Form.useWatch("diabeticRisk", form);
  const obesityRisk = Form.useWatch("obesityRisk", form);
  const cardiacRisk = Form.useWatch("cardiacRisk", form);
  const hasNcd = retrieveItem("has_NCD");

  const { result: myOrg } = useSelector(getMyOrganization);
  const viewReadingsByPerson = Form.useWatch(
    "view_readings_by_person",
    form
  );

  const onFinish = (values: any) => {};
  const marks = {
    1: 1,
    20: 20,
    40: 40,
    60: 60,
    80: 80,
    100: 100,
    120: 120,
  };

  const onValueChange = (val: any, values: any) => {
    // The form field is registered as "view_readings_by_person"; antd keys the
    // changed-values object by that exact name, so reading a camelCase key here
    // never matched and the risk fields were left as-is when switching back to
    // "All Reading". showOnlyLastReading is no longer derived here — see the
    // effect below, which keeps it in step with the field's current value.
    if (val?.view_readings_by_person === "all_reading") {
      form.setFieldsValue({
        hypertensionRisk: "na",
        hypotensionRisk: "na",
        obesityRisk: "na",
        respiratoryRisk: "na",
        cardiacRisk: "na",
        diabeticRisk: "na",
      });
    }
  };

  const handleButtonClick = (from: number, to: number) => {
    setSliderValue([from, to]);
    form.setFieldsValue({ age: [from, to] });
  };
  const handleChangeSlider = (values: number[]) => {
    setSliderValue([values?.[0], values?.[1]]);
    form.setFieldsValue({ age: values });
  };

  // Derive the "one row per subject" flag from the watched field rather than
  // from the change event, so it also tracks programmatic resets and cannot
  // drift out of sync with what the Select is showing. It feeds
  // showOnlyLastReadingOfSubject on the reading query, which is what makes the
  // All Reading / Last Reading counts differ.
  useEffect(() => {
    if (viewReadingsByPerson === "last_reading") {
      setShowOnlyLastReading(true);
      setRiskFilterCategory("custom");
    } else if (viewReadingsByPerson === "all_reading") {
      setShowOnlyLastReading(false);
      setRiskFilterCategory("");
    }
  }, [viewReadingsByPerson]);

  const getId = useMemo(() => {
    if (organizationId?.length > 0) {
      return (
        organizationId[organizationId.length - 1] ||
        organizationId[organizationId.length - 2] ||
        myOrg?.id
      );
    }
    return myOrg?.id;
  }, [organizationId, myOrg]);

  // Only send an age filter when the user has actually narrowed the slider.
  //
  // The slider is seeded at its full range, so sending fromAge/toAge unconditionally applied
  // an age filter nobody asked for. Server-side both get-subject-filter-condition.bl.ts and
  // get-reading-filter-condition.bl.ts turn fromAge/toAge into a `dob BETWEEN ...` predicate,
  // and a Prisma range on the nullable `dob` column ALSO excludes rows where dob IS NULL --
  // so subjects with no recorded date of birth, plus anyone under AGE_SLIDER_MIN years old,
  // vanished from both the list and its totalCount. For HCLF that silently hid 562 people and
  // ~500 readings from the home-page badges even though the rows were present in the database.
  const ageFilter = useMemo<{ fromAge?: number; toAge?: number }>(() => {
    const from = sliderValue?.[0] ?? AGE_SLIDER_MIN;
    const to = sliderValue?.[1] ?? AGE_SLIDER_MAX;
    if (from === AGE_SLIDER_MIN && to === AGE_SLIDER_MAX) return {};
    return { fromAge: from, toAge: to };
  }, [sliderValue]);

  const commonFilters = useMemo(() => {
    const fromD = date?.[0];
    const toD = date?.[1];
    return {
      organizationId: getId as string,
      fromDate: (fromD as { toDate?: () => Date } | undefined)?.toDate?.() ?? fromD,
      toDate: (toD as { toDate?: () => Date } | undefined)?.toDate?.() ?? toD,
      fromAge: sliderValue?.[0] ?? 1,
      toAge: sliderValue?.[1] ?? 120,
      gender: gender,
    };
  }, [getId, date, sliderValue, gender]);

  useEffect(() => {
    if (!getId) return;
    if (!commonFilters.fromDate || !commonFilters.toDate) return;

    const ncdRiskFilterForStore = {
      ...commonFilters,
      levelAttributeValueList: levelAttributeId,
      ncdConfirmationType: ncd_confirmation_type || "",
    };

    dispatch(
      dynamicSet("ncd_risk_analysis", {
        ncdRiskFilter: ncdRiskFilterForStore,
      })
    );

    const ncdAnalysisFilter: NcdRiskAnalysisFilterInput = {
      organizationId: commonFilters.organizationId,
      fromDate: commonFilters.fromDate as Date,
      toDate: commonFilters.toDate as Date,
      fromAge: commonFilters.fromAge,
      toAge: commonFilters.toAge,
      gender: commonFilters.gender,
      ncdConfirmationType: ncd_confirmation_type || "",
    };

    dispatch(
      getNcdRiskAnalysisRequest({
        filter: ncdAnalysisFilter,
      })
    );
  }, [
    getId,
    commonFilters,
    levelAttributeId,
    ncd_confirmation_type,
    dispatch,
  ]);

  useEffect(() => {
    if (!getId) return;
    dispatch(
      getScreeningStatsRequest({
        filter: {
          gender: gender,
          organizationId: getId,
          ncdConfirmationType: ncd_confirmation_type || "",
        },
      })
    );
  }, [getId, gender, ncd_confirmation_type]);

  useEffect(() => {
    if (!getId) return;
    dispatch(
      getSubjectListRequest({
        filter: {
          gender: gender,
          levelAttributeValueList: levelAttributeId,
          organizationId: getId,
          ...ageFilter,
          ncdConfirmedType: ncd_confirmation_type || "",
        },
        pagination: { pageLimit: 35, pageNumber: 1 },
      })
    );
  }, [getId, gender, levelAttributeId, ageFilter, ncd_confirmation_type]);

  useEffect(() => {
    let currentDate = moment().format("YYYY-MM-DD");
    let pastMonthDate = moment().subtract(1, "month").format("YYYY-MM-DD");
    form.setFieldValue("date", [
      dayjs(pastMonthDate, "YYYY-MM-DD"),
      dayjs(currentDate, "YYYY-MM-DD"),
    ]);
    form.setFieldValue("view_readings_by_person", "all_reading");

    // Set default values for all risk fields to "na"
    form.setFieldsValue({
      respiratoryRisk: "na",
      hypertensionRisk: "na",
      hypotensionRisk: "na",
      diabeticRisk: "na",
      obesityRisk: "na",
      cardiacRisk: "na",
    });
  }, []);

  useEffect(() => {
    // Always clear pending debounce timer to prevent stale requests from firing
    if (debounceTimerRef.current) {
      window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = undefined;
    }
    if (!getId) return;
    const fromD = date?.[0];
    const toD = date?.[1];
    const filter: ReadingFilterInput = {
      ...ageFilter,
      gender,
      subjectOrganizationId: getId,
      levelAttributeValueList: levelAttributeId,
      fromDatetime:
        (fromD as { toDate?: () => Date } | undefined)?.toDate?.() ?? fromD,
      toDatetime:
        (toD as { toDate?: () => Date } | undefined)?.toDate?.() ?? toD,
      respiratoryRisk: respiratoryRisk || "",
      hypertensionRisk: hypertensionRisk || "",
      hypotensionRisk: hypotensionRisk || "",
      diabeticRisk: diabeticRisk || "",
      obesityRisk: obesityRisk || "",
      cardiacRisk: cardiacRisk || "",
      showOnlyLastReadingOfSubject: showOnlyLastReading,
      ncdConfirmationType: ncd_confirmation_type,
      riskFilterCategory: riskFilterCategory,
    };

    const riskKeys = [
      "respiratoryRisk",
      "hypertensionRisk",
      "hypotensionRisk",
      "diabeticRisk",
      "obesityRisk",
      "cardiacRisk",
    ] as const;

    // A preset (Only DM / Only HT / DM & HT) replaces the per-risk toggles, which
    // are hidden while it is selected. Their old values would override it on the
    // server.
    const isRiskPreset = !!riskFilterCategory && riskFilterCategory !== "custom";

    riskKeys.forEach((key) => {
      const value = filter[key];

      if (isRiskPreset) {
        delete filter[key];
      } else if (riskFilterCategory === "custom" && ncd_confirmation_type) {
        if (value === "na") delete filter[key];
      } else {
        if (value === "na" || value === "") delete filter[key];
      }
    });

    // This one filter drives the first page, the later pages and the export. The
    // first page used to drop riskFilterCategory while the others kept it, so a
    // preset changed nothing until page 2, where the rows and count suddenly moved.
    dispatch(dynamicSet("reading_filter", { filter }));

    debounceTimerRef.current = window.setTimeout(() => {
      dispatch(
        getReadingListRequest({
          filter,
          pagination: { pageLimit: 10, pageNumber: 1 },
          sort: [{ field: "datetime", order: "desc" }],
        })
      );
    }, 300);
  }, [
    getId,
    ageFilter,
    date,
    gender,
    levelAttributeId,
    myOrg?.id,
    respiratoryRisk,
    hypertensionRisk,
    hypotensionRisk,
    diabeticRisk,
    obesityRisk,
    cardiacRisk,
    showOnlyLastReading,
    riskFilterCategory,
    ncd_confirmation_type,
  ]);

  const toSnakeCase = (input: any) =>
    input
      ?.toString()
      .trim()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "");

  const handleSegmentChange = (val) => {
    setRiskFilterCategory(toSnakeCase(val));
  };

  return (
    <>
      <style>{customStyles}</style>
      <div
        className="filter-container reading-filter-side-bar"
        style={{ padding: "0px" }}
      >
        <div>
          <Row
            className="header"
            align="middle"
            style={{ boxShadow: "none", backgroundColor: "#7a1443" }}
          >
            <Col span={24}>
              <Title level={4} className="client-filters-header-title">
                Filters
              </Title>
            </Col>
          </Row>

          <Form
            form={organizationForm}
            layout="vertical"
            className="reading-filter-form"
            style={{ minHeight: "auto" }}
          >
            <Row gutter={16}>
              <DynamicTreeSelect
                form={organizationForm}
                mutationType={"add"}
                isRequired={false}
                allowClear={true}
                col={24}
                className={"reading-filter-label"}
                showLevelAttribute={true}
              />
            </Row>
          </Form>
          <Form
            form={form}
            onFinish={onFinish}
            onValuesChange={onValueChange}
            layout="vertical"
            className="reading-filter-form"
          >
            <FormItem label="Date" name="date" className="reading-filter-label">
              <DatePicker.RangePicker format={"DD-MMM-YYYY"} />
            </FormItem>
            <FormItem
              label="Age (0-120)"
              name="age"
              className="reading-filter-label"
            >
              <Slider
                min={AGE_SLIDER_MIN}
                max={AGE_SLIDER_MAX}
                marks={marks}
                defaultValue={[1, 120]}
                range
                className="subject-slider"
                onChange={handleChangeSlider}
              />
            </FormItem>

            <div
              className="age-wise-filter-button"
              style={{ display: "flex", justifyContent: "space-around" }}
            >
              <Button
                type="primary"
                className="age-wise-button"
                onClick={() => handleButtonClick(18, 29)}
              >
                18 - 29 Yrs
              </Button>
              <Button
                type="primary"
                className="age-wise-button"
                onClick={() => handleButtonClick(30, 44)}
              >
                30 - 44 Yrs
              </Button>
              <Button
                type="primary"
                className="age-wise-button"
                onClick={() => handleButtonClick(30, 120)}
              >
                30+ Yrs
              </Button>
              <Button
                type="primary"
                className="age-wise-button"
                onClick={() => handleButtonClick(45, 120)}
              >
                45+ Yrs
              </Button>
            </div>
            <FormItem
              label="Gender"
              name="gender"
              className="reading-filter-label"
            >
              <Select
                options={filterOptions.gender}
                allowClear
                defaultValue={"All"}
                placeholder="All"
              />
            </FormItem>

            {hasNcd && (
              <FormItem
                label={t("NCD Type")}
                name="ncd_confirmation_type"
                className="reading-filter-label"
              >
                <Select
                  options={filterOptions.ncdTypeOptions}
                  defaultValue={"All"}
                  placeholder="All"
                />
              </FormItem>
            )}

            <FormItem
              label={t("view_readings_by_person")}
              name="view_readings_by_person"
              className="reading-filter-label"
            >
              <Select options={filterOptions.viewReadingsOptions} />
            </FormItem>

            {viewReadingsByPerson === "last_reading" && hasNcd && (
              <>
                <Col
                  span={24}
                  className="reading-filter-label view-readings-by-risk-heading"
                >
                  {t("view_readings_by_risk")}
                </Col>

                <NCDFilterSegmented
                  name={"NCD_confirmed_type"}
                  onChange={handleSegmentChange}
                />
              </>
            )}

            {riskFilterCategory === "custom" && hasNcd && (
              <>
                {[
                  "respiratoryRisk",
                  "hypertensionRisk",
                  "hypotensionRisk",
                  "diabeticRisk",
                  "obesityRisk",
                  "cardiacRisk",
                ].map((key) => (
                  <RiskSegment
                    key={key}
                    name={key}
                    label={key}
                    value={form.getFieldValue(key)}
                    form={form}
                    t={t}
                  />
                ))}
              </>
            )}

            {viewReadingsByPerson === "last_reading" && !hasNcd && (
              <>
                {[
                  "respiratoryRisk",
                  "hypertensionRisk",
                  "hypotensionRisk",
                  "diabeticRisk",
                  "obesityRisk",
                  "cardiacRisk",
                ].map((key) => (
                  <RiskSegment
                    key={key}
                    name={key}
                    label={key}
                    value={form.getFieldValue(key)}
                    form={form}
                    t={t}
                  />
                ))}
              </>
            )}
          </Form>
        </div>
      </div>
    </>
  );
};

export default ReadingFilterSideBar;
